/* HanysLux offer model — THE single derivation of offer state.
   Plan: .planning/hany-offer-tier2/PLAN.md §3. Read it before changing anything here.

   WHY THIS FILE EXISTS. Before it, the same tier arithmetic lived in four places
   (hl-cart-drawer.liquid, hl-home.js, hl-offer-modal.js, hl-offer-upsell.liquid) and drifted
   twice, both times shipping a lie to a shopper: B-014 (a rail telling a 7-ring/0-pick cart
   "1 free piece left" when it was one tap from $0.00 off) and B-017 (the checkout modal stopping
   a shopper who owed nothing). Liquid cannot import this, so the server render still restates the
   ladder — but every JS surface now reads from here, and §3.4's oracle executes THIS module
   rather than a fourth copy of the maths.

   NOT an ES module. These are classic scripts; `layout/theme.liquid` loads this file BEFORE both
   consumers and it publishes `window.hlOfferModel`. Consumers must gate defensively on the global
   and fall back to the server render if it is missing — a consumer that runs first would otherwise
   read `undefined` and die silently, which is exactly how CBC's returning-hero sat dead for weeks.

   TWO RULES THAT OUTRANK THE ARITHMETIC:

   1. MONEY IS SERVER TRUTH. `eligible()` proves the cart QUALIFIES; it never proves Shopify
      APPLIED anything. Only `allocState === 'exact'` may print a money claim. Everything else —
      an unrelated discount winning, a count that disagrees, an unreadable cart — renders
      non-monetary copy and lets checkout through untouched.

   2. THE ROLLOUT FLAG GATES EVERYTHING. `off` is honest no matter what the Admin side looks like,
      which is why it is the transition state during rollout and the first move in any rollback.
      An unrecognised flag value is coerced to `off`: a broken flag can only make the feature dark,
      never make it lie.
*/
window.hlOfferModel = (function () {
  'use strict';

  /* 2026-08-26: promo changed to BUY 3 GET 3. goal = paid + free = 6.
     `title` MUST match the Admin discount's title byte-for-byte — classify() identifies our
     allocation by that string alone, so a rename in Admin silently drops the cart to
     non-monetary copy with no error anywhere.
     Admin discount is named exactly "Buy 3, Get 3 Free" — VERIFIED against the live Admin config
     on 2026-08-29, not assumed. The plan was BUY3GET3 (matching the BUY3GET5 / BUY5GET8
     convention); it was created with the prose title instead. The THEME follows ADMIN here, not
     the reverse — Admin is where the money is, and a rename there silently breaks this match.
     RUNG2 is retained but DORMANT: rungsFor() only returns it for 'tier1_tier2', and the
     rollout flag is now 'tier1'. Kept so the ladder can be restored without rewriting it —
     BUY5GET8 is being deactivated in Admin, so it must not be reachable. */
  var RUNG1 = { goal: 6, paid: 3, free: 3, title: 'Buy 3, Get 3 Free' };
  var RUNG2 = { goal: 13, paid: 5, free: 8, title: 'BUY5GET8' };

  /* The rung table is a FUNCTION of the flag, not a constant. This is what keeps `tier1` honest:
     with only rung 1 in play, a 13-unit cart simply has no second rung to be short of, so no
     surface can nudge toward a discount that does not exist on the store. */
  function rungsFor(rollout) {
    if (rollout === 'tier1_tier2') return [RUNG1, RUNG2];
    if (rollout === 'tier1') return [RUNG1];
    return [];
  }

  function normalizeRollout(v) {
    return (v === 'tier1' || v === 'tier1_tier2') ? v : 'off';
  }

  /* ---------------------------------------------------------------- allocation classification
     Reads what Shopify ACTUALLY did to this cart. `cart` is a /cart.js payload (or the Liquid
     snapshot in [data-cd-offer2], which carries the same three fields).

     Counting rule: our discounts are 100% off, and Shopify SPLITS a line when a discount covers
     some units and not others — so a line carrying our allocation is either fully zeroed or is a
     separate line from the units that are not. Summing `quantity` over the fully-zeroed lines
     that carry our title is therefore exact, and it is deliberately NOT "every $0.00 unit":
     a genuinely free product or an unrelated gift would inflate that. */
  function classify(cart, rungs) {
    if (!cart || !cart.items) return { state: 'unknown', rung: null, serverFree: 0, title: null };

    var byTitle = {};
    rungs.forEach(function (r) { byTitle[r.title] = r; });

    var ourTitle = null, serverFree = 0, sawForeign = false;

    cart.items.forEach(function (it) {
      var allocs = it.line_level_discount_allocations || it.discounts || [];
      var mineOnLine = false;
      allocs.forEach(function (a) {
        var t = (a.discount_application && a.discount_application.title) || a.title || '';
        if (byTitle[t]) { mineOnLine = true; ourTitle = ourTitle || t; }
        else if (t) { sawForeign = true; }
      });
      if (mineOnLine && it.final_line_price === 0) serverFree += it.quantity;
    });

    if (!ourTitle) {
      /* No allocation of ours. Distinguish "nothing at all" from "something else won" — the
         second must NOT be treated as an un-earned cart, or the modal stops a shopper who is
         already getting money off. */
      var other = sawForeign || (cart.total_discount || 0) > 0;
      return { state: other ? 'other' : 'none', rung: null, serverFree: 0, title: null };
    }

    var rung = byTitle[ourTitle];
    return {
      state: serverFree === rung.free ? 'exact' : 'mismatch',
      rung: rung, serverFree: serverFree, title: ourTitle
    };
  }

  /* Every part of an allocation must agree before we will call it trustworthy. Any single
     disagreement downgrades to `mismatch`, which fails open everywhere: no money claim, no
     intercept, no upsell, row 0. See the call site for what each clause blocks. */
  function coherent(a, expected) {
    if (!a || !a.rung || !expected) return false;
    if (a.rung !== RUNG1 && a.rung !== RUNG2) return false;  // a canonical rung, not a lookalike
    if (a.rung.goal !== expected.goal) return false;         // the rung the cart actually qualifies for
    if (a.title !== a.rung.title) return false;              // title names that same rung
    if (a.serverFree !== a.rung.free) return false;          // the server really zeroed that many
    return true;
  }

  /* ------------------------------------------------------------------------------- derivation
     input: { units, picks, rollout, cart }  — or pass `alloc` directly when the caller already
     classified (the Liquid snapshot path). Returns everything every surface needs; no surface
     should ever recompute any of it. */
  function derive(input) {
    var rollout = normalizeRollout(input && input.rollout);
    var rungs = rungsFor(rollout);
    var units = Math.max(0, (input && input.units) || 0);
    var picks = Math.max(0, (input && input.picks) || 0);

    var alloc = input && input.alloc ? input.alloc : classify(input && input.cart, rungs);

    function eligible(R) { return units >= R.goal && picks >= R.free; }

    var expected = null, next = null;
    rungs.forEach(function (R) {
      if (eligible(R)) { expected = R; }
      else if (!next) { next = R; }
    });

    var state = alloc.state;
    /* ALLOCATION COHERENCE. Our model and the store must agree on every part of the claim, not
       just on which rung fired. `derive()` accepts `input.alloc` PASSED DIRECTLY (see above), so
       nothing about that object is trusted.

       This replaced a test that only caught `alloc.rung.goal < expected.goal` — Shopify granting
       a LOWER rung than earned. Three other false claims sailed through it:
         - granted HIGHER: an `exact` RUNG2 on an 8-unit cart left `confirmed` AND `next` both
           RUNG2, printing 8/8 while nudging her toward the rung she already held.
         - `serverFree` disagreeing with the rung: `{exact, RUNG1, serverFree: 0}` claimed five
           free with nothing actually zeroed.
         - a title naming a different rung than the rung object.
       `!a.rung` is defence for direct and future callers only — `classify()` cannot produce it,
       and the snapshot path in hl-offer-modal.js already converts `exact + rung:null` to
       `unknown` before we see it. Do not describe it in comments as a live hole; it is not. */
    if (state === 'exact' && !coherent(alloc, expected)) state = 'mismatch';
    /* Evidence attached to `none` is itself incoherent, and it is the dangerous direction: `none`
       is the ONLY state that may stop a shopper, so a malformed payload must never be able to
       intercept someone who may in fact hold the offer. */
    if (state === 'none' && (alloc.rung || alloc.title || alloc.serverFree)) state = 'mismatch';
    if (rollout === 'off') state = state === 'unknown' ? 'unknown' : state;

    var confirmed = state === 'exact' ? alloc.rung : null;
    var freeNow = confirmed ? confirmed.free : 0;
    var paysFor = Math.max(0, units - freeNow);

    var unitsLeft = next ? Math.max(0, next.goal - units) : 0;
    var picksLeft = next ? Math.max(0, next.free - picks) : 0;
    var slotsNeed = Math.max(unitsLeft, picksLeft);
    var paidLeft = next ? Math.max(0, next.paid - paysFor) : 0;
    var freeLeft = next ? Math.max(0, next.free - freeNow) : 0;

    /* Pips: never regress, never light a slot the discount cannot zero.

       FILLED tracks what the server actually zeroed. TOTAL is the track she is climbing — so once
       a rung is confirmed and another remains, the track WIDENS to that next rung and the extra
       slots render unlit (operator's ask 2026-08-19: "add 3 more unfilled dots and say 5/8 free").
       At 8-12 that is 5/8; at 13+ `next` is null and it closes at 8/8; under `tier1` there is no
       second rung, so it stays 5/5 exactly as before.

       Two rejected alternatives, both of which LIE, kept here because they look reasonable:
       `next.free - slotsNeed` would drop 5/5 to 3/8 the instant tier 1 completes — a visible
       regression of pieces she has already earned. `min(picks, next.free)` would show 8/8 at 12
       units, lighting three slots nothing has zeroed. Widening TOTAL alone does neither: filled
       still equals what the discount actually took off. */
    var pipTotal, pipFilled;
    if (confirmed) { pipTotal = next ? next.free : confirmed.free; pipFilled = confirmed.free; }
    else { pipTotal = rungs.length ? rungs[0].free : 0; pipFilled = Math.max(0, pipTotal - slotsNeed); }

    /* Dots once the paid gate is met, a bar while it is still open. ONE definition, because three
       surfaces need it: snippets/hl-cart-drawer.liquid already said `confirmed or paid_left == 0`,
       while hl-home.js and hl-offer-modal.js each open-coded a test that happened to agree only
       while pipTotal === pipFilled — which the widened track above breaks. */
    var gateMet = !!confirmed || paidLeft === 0;

    /* WHICH SHELF the next tap sends her to — ONE answer, read by the drawer AND the modal.
       Before this existed each surface decided for itself and they contradicted each other in
       the same cart. It encodes the operator's two-step ask (2026-08-19, "pick her 2 non discount
       ones then pick her discount ones"): while she still owes PAID units the paid shelf comes
       first; once the paid gate is met, everything left is free pieces to claim.

       The picks shelf is the safe default for every non-paid state because discount-collection is
       a strict subset of offer-eligible — anything added there advances BOTH the unit count and
       the pick count, so it can never strand a cart. An earlier draft split this three ways to
       handle "units short but every pick already held" (e.g. 10 units / 8 picks); that produced
       `paidLeft === 0` on the paid branch and rendered "Add 0 more pieces". The labels carry no
       numbers, so two branches are both simpler and correct. */
    var ctaKind = null;
    if (rollout !== 'off' && next) ctaKind = paidLeft > 0 ? 'paid' : 'picks';
    var ctaHref = ctaKind ? UPSELL_HREF[ctaKind] : null;
    var ctaLabel = ctaKind ? (ctaKind === 'paid' ? UPSELL_COPY.ctaPaid : UPSELL_COPY.ctaPicks) : null;

    var mayClaimMoney = rollout !== 'off' && state === 'exact';

    /* ONE checkout decision, three names. Deriving the booleans from a single enum makes them
       mutually exclusive STRUCTURALLY rather than as a side effect of 'exact' !== 'none'.
       - intercept: she has earned nothing and we can see that clearly. Unchanged meaning.
       - upsell:    she HAS a rung and another is still reachable.
       Everything else — mismatch, other, unknown, flag off, an empty cart — is 'none' and the
       click proceeds untouched. Nothing in this path may ever be why a shopper cannot check out.

       NOTE the upsell is NOT scoped to 8-12 units. `eligible()` needs units AND picks, so a
       13-unit cart holding only 6 picks still has RUNG2 as `next` and still gets nudged — she is
       two picks from 8 free instead of 5. That is deliberate. */
    var checkoutMode = 'none';
    if (rollout !== 'off' && state === 'none' && units >= 1) checkoutMode = 'intercept';
    else if (rollout !== 'off' && state === 'exact' && next) checkoutMode = 'upsell';
    var mayIntercept = checkoutMode === 'intercept';
    var mayUpsell = checkoutMode === 'upsell';

    var picksShort = unitsLeft === 0 && picksLeft > 0 && !confirmed;
    /* One piece from the finish line, and that piece really does complete it. The Liquid adds a
       one-tap "add the cheapest qualifying piece" button here; the button stands down when more
       than one slot is missing, because then it would reach `goal` units and still deliver $0. */
    var cliffish = !!next && units === next.goal - 1 && slotsNeed <= 1;

    var row;
    if (rollout === 'off' || state === 'mismatch' || state === 'other' || state === 'unknown') row = 0;
    else if ((expected && state === 'none') || picksShort) row = 1;
    else if (confirmed && !next) row = 2;
    else if (confirmed && paidLeft > 0) row = '3a';
    else if (confirmed && slotsNeed > 0) row = '3b';
    else if (confirmed) row = 2;
    else if (units === 0) row = 4;
    else if (paidLeft > 0) row = 5;
    else if (pipFilled === 0) row = 6;
    else if (cliffish) row = 7;
    else row = 8;

    return {
      rollout: rollout, allocState: state, appliedTitle: alloc.title,
      serverFree: alloc.serverFree, units: units, picks: picks,
      confirmed: confirmed, next: next, expected: expected,
      freeNow: freeNow, paysFor: paysFor,
      unitsLeft: unitsLeft, picksLeft: picksLeft, slotsNeed: slotsNeed,
      paidLeft: paidLeft, freeLeft: freeLeft,
      pipTotal: pipTotal, pipFilled: pipFilled, gateMet: gateMet,
      ctaKind: ctaKind, ctaHref: ctaHref, ctaLabel: ctaLabel,
      picksShort: picksShort, cliffish: cliffish,
      mayClaimMoney: mayClaimMoney, checkoutMode: checkoutMode,
      mayIntercept: mayIntercept, mayUpsell: mayUpsell, row: row
    };
  }

  /* Copy. Authored by agents/ecomm_copywriter.py 2026-08-19 (registry Rule 2 — the site engineer
     does not write shopper-facing strings). Placeholders are filled from the derived state, so a
     number can never disagree with the sentence around it. */
  var COPY = {
    row0: 'Check out our discount collection for pieces picked just for you.',
    row2: 'You unlocked {freeNow} free! 🎉 You only pay for {paysFor}.',
    row3a: 'Add {paidLeft} more, get {freeLeft} more free',
    row3b: 'You’re in! Just pick {slotsNeed} more free pieces — no extra cost',
    cta: 'Pick my free pieces'
  };

  /* The same sentences with the emphasis the drawer renders. hl-home.js paints from HERE, never
     from hand-built strings, so the client re-render cannot drift from the copy table; and
     snippets/hl-cart-drawer.liquid mirrors these byte for byte, which PLAN §4 F2 verifies by
     diffing the rendered drawer against this module. Stripping the tags must reproduce COPY
     exactly — the oracle asserts it, so an edit to one without the other fails the suite. */
  var COPY_HTML = {
    row0: 'Check out our discount collection for pieces picked just for you.',
    row2: 'You unlocked <b>{freeNow}</b> free! 🎉 You only pay for {paysFor}.',
    row3a: 'Add <b>{paidLeft} more</b>, get <b>{freeLeft} more free</b>',
    row3b: 'You’re in! Just pick <b>{slotsNeed}</b> more free pieces — no extra cost',
    cta: 'Pick my free pieces'
  };

  /* CHECKOUT UPSELL copy. Authored by agents/ecomm_copywriter.py 2026-08-19 (registry Rule 2).
     Lives here rather than in hl-offer-modal.js so the oracle can prove no placeholder ever
     reaches a shopper unresolved, and so `{pieceWord}` cannot drift from the count beside it.

     TWO CONSTRAINTS BAKED INTO THESE STRINGS, both from the plan review — do not "improve" them:
     1. NOTHING here claims an outcome from visiting the destination. The paid step sends her to a
        NORMAL collection (Belly Bars, operator's choice 2026-08-19) where 4 of 100 active products
        are bundles outside `offer-eligible` and advance nothing. So the copy states the ladder and
        where she stands — never "add 2 from this page and you get 3 free".
     2. Counts are plural-safe. `paidLeft` is 1 or 2 and `slotsNeed` is 1-3, so every count-adjacent
        noun uses {pieceWord}. "1 qualifying pieces" is a defect, not a typo. */
  var UPSELL_COPY = {
    /* The two CTA labels are shared by the DRAWER and the MODAL on purpose. They used to be
       authored separately, and the surfaces drifted into contradicting each other: at 8 units the
       drawer said "Pick my free pieces" and sent her to the Discount Collection while the modal
       said "Shop Belly Bars" — same cart, opposite instructions. One label, one destination. */
    ctaPaid:    'Shop Belly Bars',
    ctaPicks:   'Pick Your Free Pieces',
    /* No "tier"/"rung" anywhere: shoppers do not think in tiers, and "2 qualifying pieces to
       tier 2" is internal framing that reached the live modal once already. */
    paidTitle:  'Add {paidLeft} more {pieceWord} for the bigger deal',
    /* This sentence carries the one thing the maths cannot: that continuing is OPTIONAL. The
       eyebrow above already states the deal, so it is deliberately not restated here.

       It briefly also spelled out which products count ("Only belly rings and jewelry in the offer
       count, bundles don't"). Operator cut it 2026-08-19: by the time this modal fires she has
       already built a qualifying cart and claimed five free pieces, so she has learned what counts
       by doing it — the caveat was answering a question she no longer has. The 4 non-eligible
       bundles on the Belly Bars shelf remain a known, accepted gap (see the traps below); what
       still holds is that NOTHING here promises an outcome from visiting that shelf. */
    paidSub:    'Totally optional — check out now if you’re ready.',
    picksTitle: 'You’ve got {slotsNeed} free {pieceWord} waiting',
    picksSub:   'These are on us and come off at checkout. Skip them and check out now if you’d rather.',
    skip:       'Continue to checkout'
  };

  /* The two destinations, kept beside the copy that points at them so a handle change cannot
     silently outlive its sentence. `paid` is the operator's 2026-08-19 choice; `picks` is the
     discount's customerGets scope and is all-qualifying by construction. */
  var UPSELL_HREF = { paid: '/collections/real-belly-piercings', picks: '/collections/discount-collection' };

  /* The word that must agree with the count printed next to it. */
  function pieceWord(n) { return n === 1 ? 'piece' : 'pieces'; }

  function stripTags(s) { return String(s).replace(/<[^>]+>/g, ''); }

  function fill(tpl, st) {
    return String(tpl).replace(/\{(\w+)\}/g, function (_, k) {
      return st[k] === undefined || st[k] === null ? '' : String(st[k]);
    });
  }

  return {
    RUNG1: RUNG1, RUNG2: RUNG2, COPY: COPY, COPY_HTML: COPY_HTML, stripTags: stripTags,
    UPSELL_COPY: UPSELL_COPY, UPSELL_HREF: UPSELL_HREF, pieceWord: pieceWord,
    rungsFor: rungsFor, normalizeRollout: normalizeRollout,
    classify: classify, coherent: coherent, derive: derive, fill: fill
  };
})();
